import { useState, useEffect } from "react";
import api, { getUploadUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Settings, Building2, Hash, Image, Save, Plus, Edit, Trash2, Loader2, CheckCircle, AlertTriangle, RefreshCw, Download, Upload, Database, Mail, Send, MapPin, Shield, HardDrive } from "lucide-react";
import { toast } from "sonner";
import StorageManagement from "../components/StorageManagement";

export default function Configuration() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    numero_control_actual: 1,
    productor_nombre: "INVERSIONES FRISUR, C.A.",
    productor_rif: "J-41253799-7",
    productor_spes: "SPES-6479",
    productor_direccion: "",
    productor_pais: "",
    productor_codigo_iso: "",
    empresas: [],
    logos: [],
    empresa_activa_id: null,
  });
  
  // SMTP Config
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: "",
    smtp_port: 465,
    smtp_user: "",
    smtp_password: "",
    smtp_from_email: "",
    admin_notification_email: "",
  });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  
  // Para validar número de control
  const [controlValidation, setControlValidation] = useState({ checking: false, exists: false });
  
  // Listas de clientes y proveedores para asignar logos
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  
  // Lugares de Destino
  const [lugaresDestino, setLugaresDestino] = useState([]);
  const [lugarDestinoDialogOpen, setLugarDestinoDialogOpen] = useState(false);
  const [editingLugarDestino, setEditingLugarDestino] = useState(null);
  const [lugarDestinoForm, setLugarDestinoForm] = useState({
    nombre: "",
    cif: "",
    telefono: "",
    numero_registro: "",
    direccion: "",
    pais: "",
    codigo_iso_pais: "",
  });
  
  // Puestos de Control
  const [puestosControl, setPuestosControl] = useState([]);
  const [puestoControlDialogOpen, setPuestoControlDialogOpen] = useState(false);
  const [editingPuestoControl, setEditingPuestoControl] = useState(null);
  const [puestoControlForm, setPuestoControlForm] = useState({
    nombre: "",
    codigo: "",
  });
  
  // Dialog states
  const [empresaDialogOpen, setEmpresaDialogOpen] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [editingLogo, setEditingLogo] = useState(null);
  const [confirmControlDialog, setConfirmControlDialog] = useState(false);
  
  // Form states
  const [empresaForm, setEmpresaForm] = useState({
    nombre: "",
    cif: "",
    rgseaa: "",
    cuenta_bancaria: "",
    registro_mercantil: "",
    direccion: "",
    telefono: "",
    email: "",
    tipo: "importador",
    logo_url: "",
  });
  const [logoForm, setLogoForm] = useState({
    nombre: "",
    url: "",
    tipo: "",
    entidad_id: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [empresaLogoFile, setEmpresaLogoFile] = useState(null);
  
  // Backup states
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [includeDocuments, setIncludeDocuments] = useState(false);

  useEffect(() => {
    fetchData();
    fetchSmtpConfig();
    fetchLugaresDestino();
    fetchPuestosControl();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, clientsRes, suppliersRes] = await Promise.all([
        api.get("/config").catch(() => ({ data: null })),
        api.get("/clients"),
        api.get("/suppliers"),
      ]);
      if (configRes.data) {
        setConfig(configRes.data);
      }
      setClients(clientsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error("Error loading config:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchLugaresDestino = async () => {
    try {
      const res = await api.get("/lugares-destino");
      setLugaresDestino(res.data);
    } catch (error) {
      console.error("Error loading lugares destino:", error);
    }
  };
  
  const fetchPuestosControl = async () => {
    try {
      const res = await api.get("/puestos-control");
      setPuestosControl(res.data);
    } catch (error) {
      console.error("Error loading puestos control:", error);
    }
  };
  
  const fetchSmtpConfig = async () => {
    try {
      setSmtpLoading(true);
      const res = await api.get("/config/smtp");
      setSmtpConfig(res.data);
    } catch (error) {
      console.error("Error loading SMTP config:", error);
    } finally {
      setSmtpLoading(false);
    }
  };
  
  const handleSaveSmtpConfig = async () => {
    try {
      setSmtpSaving(true);
      await api.put("/config/smtp", smtpConfig);
      toast.success("Configuración SMTP guardada correctamente");
    } catch (error) {
      console.error("Error saving SMTP config:", error);
      toast.error("Error al guardar la configuración SMTP");
    } finally {
      setSmtpSaving(false);
    }
  };
  
  const handleTestSmtp = async () => {
    try {
      setSmtpTesting(true);
      const res = await api.post("/config/smtp/test");
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.error("Error testing SMTP:", error);
      toast.error("Error al enviar email de prueba");
    } finally {
      setSmtpTesting(false);
    }
  };

  const validateControlNumber = async (numero) => {
    if (!numero) return;
    setControlValidation({ checking: true, exists: false });
    try {
      const response = await api.get(`/config/validate-control/${numero}`);
      setControlValidation({ checking: false, exists: response.data.exists });
    } catch (error) {
      setControlValidation({ checking: false, exists: false });
    }
  };

  const handleSaveConfig = async () => {
    // Si el número de control ya existe, pedir confirmación
    if (controlValidation.exists) {
      setConfirmControlDialog(true);
      return;
    }
    await saveConfig();
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/config", config);
      toast.success("Configuración guardada correctamente");
      setConfirmControlDialog(false);
    } catch (error) {
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmpresa = async () => {
    if (!empresaForm.nombre || !empresaForm.cif) {
      toast.error("Nombre y CIF son obligatorios");
      return;
    }
    
    let logoUrl = empresaForm.logo_url;
    
    // Si hay un archivo de logo, subirlo
    if (empresaLogoFile) {
      const formData = new FormData();
      formData.append("file", empresaLogoFile);
      formData.append("tipo", "empresa");
      
      try {
        const response = await api.post("/config/upload-logo", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        logoUrl = response.data.url;
      } catch (error) {
        toast.error("Error al subir el logo");
        return;
      }
    }
    
    const empresas = [...config.empresas];
    const empresaData = { ...empresaForm, logo_url: logoUrl };
    
    if (editingEmpresa) {
      const idx = empresas.findIndex(e => e.id === editingEmpresa.id);
      if (idx >= 0) {
        empresas[idx] = { ...empresaData, id: editingEmpresa.id };
      }
    } else {
      empresas.push({ ...empresaData, id: Date.now().toString(), activa: empresas.length === 0 });
    }
    
    const newConfig = { ...config, empresas };
    // Si es la primera empresa, establecerla como activa
    if (!config.empresa_activa_id && empresas.length > 0) {
      newConfig.empresa_activa_id = empresas[0].id;
    }
    
    setConfig(newConfig);
    
    try {
      await api.put("/config", newConfig);
      toast.success(editingEmpresa ? "Empresa actualizada" : "Empresa añadida");
      setEmpresaDialogOpen(false);
      resetEmpresaForm();
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleSaveLogo = async () => {
    if (!logoForm.nombre) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!logoForm.entidad_id) {
      toast.error("Debes asignar el logo a un proveedor o cliente");
      return;
    }
    
    let logoUrl = logoForm.url;
    
    // Si hay un archivo, subirlo
    if (logoFile) {
      const formData = new FormData();
      formData.append("file", logoFile);
      formData.append("tipo", logoForm.tipo);
      
      try {
        const response = await api.post("/config/upload-logo", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        logoUrl = response.data.url;
      } catch (error) {
        toast.error("Error al subir el logo");
        return;
      }
    }
    
    if (!logoUrl) {
      toast.error("Debes subir una imagen o proporcionar una URL");
      return;
    }
    
    const logos = [...config.logos];
    const logoData = { ...logoForm, url: logoUrl };
    
    if (editingLogo) {
      const idx = logos.findIndex(l => l.id === editingLogo.id);
      if (idx >= 0) {
        logos[idx] = { ...logoData, id: editingLogo.id };
      }
    } else {
      logos.push({ ...logoData, id: Date.now().toString() });
    }
    
    const newConfig = { ...config, logos };
    setConfig(newConfig);
    
    try {
      await api.put("/config", newConfig);
      toast.success(editingLogo ? "Logo actualizado" : "Logo añadido");
      setLogoDialogOpen(false);
      resetLogoForm();
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleSetEmpresaActiva = async (empresaId) => {
    const newConfig = { ...config, empresa_activa_id: empresaId };
    setConfig(newConfig);
    try {
      await api.put("/config", newConfig);
      toast.success("Empresa activa cambiada. Recargando datos...");
      // Recargar la página después de 500ms para refrescar todos los datos
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      toast.error("Error al cambiar empresa activa");
    }
  };

  const handleMigrateBatches = async (empresaId) => {
    if (!window.confirm("¿Migrar todos los lotes y documentos sin empresa asignada a esta empresa? Esta acción es irreversible.")) {
      return;
    }
    try {
      const response = await api.post(`/config/migrate-batches/${empresaId}`);
      toast.success(`Migración completada: ${response.data.batches_updated} lotes y ${response.data.documents_updated} documentos actualizados`);
    } catch (error) {
      toast.error("Error al migrar datos");
    }
  };

  const handleDelete = async () => {
    if (!deleteType) return;
    
    let newConfig = { ...config };
    if (deleteType.type === "empresa") {
      newConfig.empresas = config.empresas.filter(e => e.id !== deleteType.id);
      if (config.empresa_activa_id === deleteType.id && newConfig.empresas.length > 0) {
        newConfig.empresa_activa_id = newConfig.empresas[0].id;
      }
    } else if (deleteType.type === "logo") {
      newConfig.logos = config.logos.filter(l => l.id !== deleteType.id);
    }
    
    setConfig(newConfig);
    
    try {
      await api.put("/config", newConfig);
      toast.success("Eliminado correctamente");
    } catch (error) {
      toast.error("Error al eliminar");
    }
    
    setDeleteType(null);
  };

  const openEditEmpresa = (empresa) => {
    setEditingEmpresa(empresa);
    setEmpresaForm(empresa);
    setEmpresaLogoFile(null);
    setEmpresaDialogOpen(true);
  };

  const openEditLogo = (logo) => {
    setEditingLogo(logo);
    setLogoForm(logo);
    setLogoFile(null);
    setLogoDialogOpen(true);
  };

  const resetEmpresaForm = () => {
    setEditingEmpresa(null);
    setEmpresaForm({
      nombre: "",
      cif: "",
      rgseaa: "",
      cuenta_bancaria: "",
      registro_mercantil: "",
      direccion: "",
      telefono: "",
      email: "",
      tipo: "importador",
      logo_url: "",
    });
    setEmpresaLogoFile(null);
  };

  const resetLogoForm = () => {
    setEditingLogo(null);
    setLogoForm({ nombre: "", url: "", tipo: "", entidad_id: "" });
    setLogoFile(null);
  };

  const getEntidadNombre = (tipo, id) => {
    if (tipo === "proveedor") {
      const s = suppliers.find(s => s.id === id);
      return s?.nombre || id;
    } else if (tipo === "cliente") {
      const c = clients.find(c => c.id === id);
      return c?.nombre || id;
    }
    return id;
  };

  // ========== LUGARES DE DESTINO CRUD ==========
  const handleSaveLugarDestino = async () => {
    if (!lugarDestinoForm.nombre) {
      toast.error("El nombre es obligatorio");
      return;
    }
    
    try {
      if (editingLugarDestino) {
        await api.put(`/lugares-destino/${editingLugarDestino.id}`, lugarDestinoForm);
        toast.success("Lugar de destino actualizado");
      } else {
        await api.post("/lugares-destino", lugarDestinoForm);
        toast.success("Lugar de destino añadido");
      }
      setLugarDestinoDialogOpen(false);
      resetLugarDestinoForm();
      fetchLugaresDestino();
    } catch (error) {
      toast.error("Error al guardar lugar de destino");
    }
  };

  const handleDeleteLugarDestino = async (id) => {
    if (!window.confirm("¿Eliminar este lugar de destino?")) return;
    try {
      await api.delete(`/lugares-destino/${id}`);
      toast.success("Lugar de destino eliminado");
      fetchLugaresDestino();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openEditLugarDestino = (lugar) => {
    setEditingLugarDestino(lugar);
    setLugarDestinoForm({
      nombre: lugar.nombre || "",
      cif: lugar.cif || "",
      telefono: lugar.telefono || "",
      numero_registro: lugar.numero_registro || "",
      direccion: lugar.direccion || "",
      pais: lugar.pais || "",
      codigo_iso_pais: lugar.codigo_iso_pais || "",
    });
    setLugarDestinoDialogOpen(true);
  };

  const resetLugarDestinoForm = () => {
    setEditingLugarDestino(null);
    setLugarDestinoForm({
      nombre: "",
      cif: "",
      telefono: "",
      numero_registro: "",
      direccion: "",
      pais: "",
      codigo_iso_pais: "",
    });
  };

  // ========== PUESTOS DE CONTROL CRUD ==========
  const handleSavePuestoControl = async () => {
    if (!puestoControlForm.nombre) {
      toast.error("El nombre es obligatorio");
      return;
    }
    
    try {
      if (editingPuestoControl) {
        await api.put(`/puestos-control/${editingPuestoControl.id}`, puestoControlForm);
        toast.success("Puesto de control actualizado");
      } else {
        await api.post("/puestos-control", puestoControlForm);
        toast.success("Puesto de control añadido");
      }
      setPuestoControlDialogOpen(false);
      resetPuestoControlForm();
      fetchPuestosControl();
    } catch (error) {
      toast.error("Error al guardar puesto de control");
    }
  };

  const handleDeletePuestoControl = async (id) => {
    if (!window.confirm("¿Eliminar este puesto de control?")) return;
    try {
      await api.delete(`/puestos-control/${id}`);
      toast.success("Puesto de control eliminado");
      fetchPuestosControl();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openEditPuestoControl = (puesto) => {
    setEditingPuestoControl(puesto);
    setPuestoControlForm({
      nombre: puesto.nombre || "",
      codigo: puesto.codigo || "",
    });
    setPuestoControlDialogOpen(true);
  };

  const resetPuestoControlForm = () => {
    setEditingPuestoControl(null);
    setPuestoControlForm({
      nombre: "",
      codigo: "",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const empresaActiva = config.empresas.find(e => e.id === config.empresa_activa_id);

  return (
    <div className="space-y-6" data-testid="configuration-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuración</h1>
          <p className="text-slate-500 mt-1">Gestiona los datos generales del sistema</p>
        </div>
        {empresaActiva && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
            <p className="text-xs text-green-600">Empresa activa:</p>
            <p className="font-semibold text-green-800">{empresaActiva.nombre}</p>
          </div>
        )}
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="empresas" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Empresas
          </TabsTrigger>
          <TabsTrigger value="lugares-destino" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Lugares Destino
          </TabsTrigger>
          <TabsTrigger value="puestos-control" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Puestos Control
          </TabsTrigger>
          <TabsTrigger value="logos" className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            Logos
          </TabsTrigger>
          <TabsTrigger value="smtp" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email (SMTP)
          </TabsTrigger>
          <TabsTrigger value="backup" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Copia de Seguridad
          </TabsTrigger>
          <TabsTrigger value="almacenamiento" className="flex items-center gap-2">
            <HardDrive className="w-4 h-4" />
            Almacenamiento
          </TabsTrigger>
        </TabsList>

        {/* Tab General */}
        <TabsContent value="general">
          <div className="grid gap-6">
            {/* Datos del Productor */}
            <Card>
              <CardHeader>
                <CardTitle>PRODUCIDO POR</CardTitle>
                <CardDescription>
                  Estos datos aparecerán en facturas, packing list y etiquetas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Productor</Label>
                    <Input
                      value={config.productor_nombre || ""}
                      onChange={(e) => setConfig({ ...config, productor_nombre: e.target.value })}
                      placeholder="INVERSIONES FRISUR, C.A."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>RIF</Label>
                    <Input
                      value={config.productor_rif || ""}
                      onChange={(e) => setConfig({ ...config, productor_rif: e.target.value })}
                      placeholder="J-41253799-7"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SPES</Label>
                    <Input
                      value={config.productor_spes || ""}
                      onChange={(e) => setConfig({ ...config, productor_spes: e.target.value })}
                      placeholder="SPES-6479"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Dirección</Label>
                    <Input
                      value={config.productor_direccion || ""}
                      onChange={(e) => setConfig({ ...config, productor_direccion: e.target.value })}
                      placeholder="Av principal de Chacachacare, local S/N, Chacachacare"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>País</Label>
                    <Input
                      value={config.productor_pais || ""}
                      onChange={(e) => setConfig({ ...config, productor_pais: e.target.value })}
                      placeholder="Venezuela"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Código ISO del País</Label>
                    <Input
                      value={config.productor_codigo_iso || ""}
                      onChange={(e) => setConfig({ ...config, productor_codigo_iso: e.target.value.toUpperCase() })}
                      placeholder="VE"
                      maxLength={2}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Numeración */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Hash className="w-5 h-5" />
                  Numeración
                </CardTitle>
                <CardDescription>
                  Configuración de números de control
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-w-xs space-y-2">
                  <Label>Próximo Número de Control (Lotes)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={String(config.numero_control_actual || 1).padStart(6, '0')}
                      onChange={(e) => {
                        // Eliminar ceros a la izquierda para parsear
                        const num = parseInt(e.target.value.replace(/^0+/, '')) || 1;
                        setConfig({ ...config, numero_control_actual: num });
                        validateControlNumber(num);
                      }}
                      className="font-mono text-center"
                      maxLength={6}
                    />
                    {controlValidation.checking && (
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    )}
                    {!controlValidation.checking && controlValidation.exists && (
                      <AlertTriangle className="w-5 h-5 text-amber-500" title="Este número ya existe" />
                    )}
                    {!controlValidation.checking && !controlValidation.exists && config.numero_control_actual > 0 && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                  {controlValidation.exists && (
                    <p className="text-xs text-amber-600">
                      ⚠️ Este número de control ya ha sido usado. Se pedirá confirmación al guardar.
                    </p>
                  )}
                  <p className="text-xs text-slate-500">
                    Este número se usará para el próximo lote creado (formato: 6 dígitos)
                  </p>
                </div>
                
                {/* Números de control de facturas a cliente por empresa */}
                {config.empresas && config.empresas.length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <h4 className="font-medium mb-3">Numeración de Facturas a Cliente (por empresa)</h4>
                    <p className="text-xs text-slate-500 mb-3">
                      Cada empresa tiene su propio contador para las facturas a cliente. 
                      El número se autoincrementará cada vez que se genere una factura a cliente.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {config.empresas.map((empresa, idx) => (
                        <div key={empresa.id} className="p-3 border rounded-lg bg-slate-50">
                          <Label className="text-sm font-medium">{empresa.nombre}</Label>
                          <div className="flex items-center gap-2 mt-2">
                            <Input
                              type="number"
                              value={empresa.next_client_invoice_number || 1}
                              onChange={(e) => {
                                const num = parseInt(e.target.value) || 1;
                                const newEmpresas = [...config.empresas];
                                newEmpresas[idx] = { ...newEmpresas[idx], next_client_invoice_number: num };
                                setConfig({ ...config, empresas: newEmpresas });
                              }}
                              min={1}
                              className="max-w-[120px]"
                            />
                            <span className="text-xs text-slate-500">Próximo nº factura cliente</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveConfig} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Configuración
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Empresas */}
        <TabsContent value="empresas">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Empresas
                </CardTitle>
                <CardDescription>
                  Gestiona las empresas importadoras. La empresa activa se usa para todas las operaciones.
                </CardDescription>
              </div>
              <Button onClick={() => { resetEmpresaForm(); setEmpresaDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nueva Empresa
              </Button>
            </CardHeader>
            <CardContent>
              {config.empresas.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No hay empresas configuradas. Añade al menos una empresa para operar.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Logo</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>CIF/NIF</TableHead>
                      <TableHead>RGSEAA</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="w-[150px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {config.empresas.map((empresa) => (
                      <TableRow key={empresa.id} className={empresa.id === config.empresa_activa_id ? "bg-green-50" : ""}>
                      <TableCell>
                          {empresa.logo_url ? (
                            <img src={getUploadUrl(empresa.logo_url)} alt={empresa.nombre} className="h-10 w-auto object-contain" />
                          ) : (
                            <div className="h-10 w-10 bg-slate-100 rounded flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{empresa.nombre}</TableCell>
                        <TableCell className="font-mono">{empresa.cif}</TableCell>
                        <TableCell className="font-mono text-xs">{empresa.rgseaa || "-"}</TableCell>
                        <TableCell>{empresa.email || "-"}</TableCell>
                        <TableCell>
                          {empresa.id === config.empresa_activa_id ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                              <CheckCircle className="w-3 h-3" /> Activa
                            </span>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetEmpresaActiva(empresa.id)}
                            >
                              Activar
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditEmpresa(empresa)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              title="Migrar datos sin empresa a esta empresa"
                              onClick={() => handleMigrateBatches(empresa.id)}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-red-600" 
                              onClick={() => setDeleteType({ type: "empresa", id: empresa.id })}
                              disabled={empresa.id === config.empresa_activa_id && config.empresas.length === 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Lugares de Destino */}
        <TabsContent value="lugares-destino">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Lugares de Destino
                </CardTitle>
                <CardDescription>
                  Gestiona los almacenes frigoríficos y destinos para las partidas
                </CardDescription>
              </div>
              <Button onClick={() => { resetLugarDestinoForm(); setLugarDestinoDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Lugar
              </Button>
            </CardHeader>
            <CardContent>
              {lugaresDestino.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No hay lugares de destino configurados
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>CIF</TableHead>
                      <TableHead>Nº Registro</TableHead>
                      <TableHead>Teléfono</TableHead>
                      <TableHead>País</TableHead>
                      <TableHead className="w-[100px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lugaresDestino.map((lugar) => (
                      <TableRow key={lugar.id}>
                        <TableCell className="font-medium">{lugar.nombre}</TableCell>
                        <TableCell className="font-mono text-xs">{lugar.cif || "-"}</TableCell>
                        <TableCell className="font-mono text-xs">{lugar.numero_registro || "-"}</TableCell>
                        <TableCell>{lugar.telefono || "-"}</TableCell>
                        <TableCell>
                          {lugar.pais && (
                            <span className="text-xs">
                              {lugar.pais} {lugar.codigo_iso_pais && `(${lugar.codigo_iso_pais})`}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditLugarDestino(lugar)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-red-600"
                              onClick={() => handleDeleteLugarDestino(lugar.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Puestos de Control */}
        <TabsContent value="puestos-control">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Puestos de Control Fronterizo
                </CardTitle>
                <CardDescription>
                  Gestiona los puestos de control fronterizo de entrada
                </CardDescription>
              </div>
              <Button onClick={() => { resetPuestoControlForm(); setPuestoControlDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Puesto
              </Button>
            </CardHeader>
            <CardContent>
              {puestosControl.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No hay puestos de control configurados
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Código</TableHead>
                      <TableHead className="w-[100px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {puestosControl.map((puesto) => (
                      <TableRow key={puesto.id}>
                        <TableCell className="font-medium">{puesto.nombre}</TableCell>
                        <TableCell className="font-mono text-xs">{puesto.codigo || "-"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditPuestoControl(puesto)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-red-600"
                              onClick={() => handleDeletePuestoControl(puesto.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Logos */}
        <TabsContent value="logos">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Logos de Proveedores y Clientes
                </CardTitle>
                <CardDescription>
                  Asigna logos a proveedores y clientes para usarlos en documentos
                </CardDescription>
              </div>
              <Button onClick={() => { resetLogoForm(); setLogoDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Logo
              </Button>
            </CardHeader>
            <CardContent>
              {config.logos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No hay logos configurados
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {config.logos.map((logo) => (
                    <div key={logo.id} className="border rounded-lg p-4 text-center">
                      <img 
                        src={getUploadUrl(logo.url)} 
                        alt={logo.nombre} 
                        className="h-16 mx-auto mb-2 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <p className="font-medium text-sm">{logo.nombre}</p>
                      <p className="text-xs text-slate-500 capitalize">{logo.tipo}</p>
                      <p className="text-xs text-slate-400 truncate">
                        {getEntidadNombre(logo.tipo, logo.entidad_id)}
                      </p>
                      <div className="flex justify-center gap-1 mt-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditLogo(logo)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => setDeleteType({ type: "logo", id: logo.id })}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab SMTP */}
        <TabsContent value="smtp">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Configuración de Email (SMTP)
                </CardTitle>
                <CardDescription>
                  Configura el servidor de correo para el envío de notificaciones automáticas.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {smtpLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Servidor SMTP</Label>
                        <Input
                          value={smtpConfig.smtp_host}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_host: e.target.value })}
                          placeholder="smtp.ejemplo.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Puerto</Label>
                        <Input
                          type="number"
                          value={smtpConfig.smtp_port}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_port: parseInt(e.target.value) || 465 })}
                          placeholder="465"
                        />
                        <p className="text-xs text-slate-500">
                          465 para SSL/TLS, 587 para STARTTLS
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Usuario SMTP</Label>
                        <Input
                          value={smtpConfig.smtp_user}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_user: e.target.value })}
                          placeholder="usuario@ejemplo.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contraseña SMTP</Label>
                        <Input
                          type="password"
                          value={smtpConfig.smtp_password}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_password: e.target.value })}
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email Remitente</Label>
                        <Input
                          type="email"
                          value={smtpConfig.smtp_from_email}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_from_email: e.target.value })}
                          placeholder="notificaciones@ejemplo.com"
                        />
                        <p className="text-xs text-slate-500">
                          Dirección que aparecerá como remitente
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Email Administrador</Label>
                        <Input
                          type="email"
                          value={smtpConfig.admin_notification_email}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, admin_notification_email: e.target.value })}
                          placeholder="admin@ejemplo.com"
                        />
                        <p className="text-xs text-slate-500">
                          Email para recibir notificaciones de proveedores
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 pt-4 border-t">
                      <Button
                        onClick={handleSaveSmtpConfig}
                        disabled={smtpSaving}
                        className="bg-slate-900 hover:bg-slate-800"
                      >
                        {smtpSaving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Guardar Configuración
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleTestSmtp}
                        disabled={smtpTesting}
                      >
                        {smtpTesting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Enviar Email de Prueba
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            
            {/* Información */}
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div className="text-sm text-slate-600">
                    <p className="font-medium text-slate-700 mb-2">Emails automáticos que envía el sistema:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li><strong>Proveedor crea lote:</strong> Email al administrador</li>
                      <li><strong>Admin asigna cliente:</strong> Email a todos los usuarios del cliente</li>
                      <li><strong>Cambio de estado:</strong> Email al cliente (y admin si lo hace proveedor)</li>
                      <li><strong>Admin modifica lote:</strong> Email a usuarios del proveedor</li>
                    </ul>
                    <p className="mt-2 text-amber-600">
                      Importante: Los usuarios deben tener el campo "Email de Contacto" configurado para recibir notificaciones.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Copia de Seguridad */}
        <TabsContent value="backup">
          <div className="grid gap-6">
            {/* Exportar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-emerald-600" />
                  Exportar Copia de Seguridad
                </CardTitle>
                <CardDescription>
                  Descarga toda la información del sistema en un archivo Excel. 
                  Incluye: lotes, productos, clientes, proveedores, operadores logísticos, 
                  compañías de transporte, usuarios y documentos.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Checkbox para incluir documentos */}
                <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <input
                    type="checkbox"
                    id="includeDocuments"
                    checked={includeDocuments}
                    onChange={(e) => setIncludeDocuments(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <div>
                    <label htmlFor="includeDocuments" className="font-medium text-blue-900 cursor-pointer">
                      Incluir documentos subidos
                    </label>
                    <p className="text-sm text-blue-700 mt-1">
                      Si marcas esta opción, se generará un archivo ZIP que incluirá todos los documentos 
                      organizados por lote y tipo de documento.
                      <span className="block mt-1 text-blue-600 font-medium">
                        📁 Estructura: documentos / [nº lote] / [tipo documento] / [archivo]
                      </span>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <Button
                    onClick={async () => {
                      setExporting(true);
                      try {
                        const endpoint = includeDocuments 
                          ? "/backup/export?include_documents=true" 
                          : "/backup/export";
                        const response = await api.get(endpoint, {
                          responseType: "blob"
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement("a");
                        link.href = url;
                        const extension = includeDocuments ? "zip" : "xlsx";
                        const prefix = includeDocuments ? "backup_completo" : "backup";
                        link.setAttribute("download", `${prefix}_${new Date().toISOString().split("T")[0]}.${extension}`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success(includeDocuments 
                          ? "Copia de seguridad completa descargada (incluye documentos)" 
                          : "Copia de seguridad descargada correctamente"
                        );
                      } catch (error) {
                        console.error("Error exporting:", error);
                        toast.error("Error al exportar la copia de seguridad");
                      } finally {
                        setExporting(false);
                      }
                    }}
                    disabled={exporting}
                    className={includeDocuments ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"}
                  >
                    {exporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {includeDocuments ? "Generando ZIP..." : "Exportando..."}
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        {includeDocuments ? "Descargar Backup Completo (ZIP)" : "Descargar Copia de Seguridad (Excel)"}
                      </>
                    )}
                  </Button>
                  
                  {includeDocuments && (
                    <span className="text-sm text-amber-600">
                      ⚠️ La descarga puede tardar varios minutos si hay muchos documentos
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Importar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-blue-600" />
                  Restaurar Copia de Seguridad
                </CardTitle>
                <CardDescription>
                  Sube un archivo Excel de copia de seguridad para restaurar los datos.
                  <span className="block mt-2 text-amber-600 font-medium">
                    ⚠️ Solo se añadirán registros nuevos. Los registros existentes NO se sobrescribirán.
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      setImporting(true);
                      setImportResult(null);
                      
                      try {
                        const formData = new FormData();
                        formData.append("file", file);
                        
                        const response = await api.post("/backup/import", formData, {
                          headers: { "Content-Type": "multipart/form-data" }
                        });
                        
                        setImportResult(response.data);
                        toast.success(response.data.message);
                        
                        // Recargar datos
                        fetchData();
                      } catch (error) {
                        console.error("Error importing:", error);
                        const errorMsg = error.response?.data?.detail || "Error al importar";
                        toast.error(errorMsg);
                        setImportResult({ success: false, message: errorMsg });
                      } finally {
                        setImporting(false);
                        e.target.value = ""; // Reset input
                      }
                    }}
                    disabled={importing}
                    className="max-w-md"
                  />
                  {importing && (
                    <div className="flex items-center gap-2 text-blue-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Importando...</span>
                    </div>
                  )}
                </div>

                {/* Resultado de la importación */}
                {importResult && (
                  <div className={`p-4 rounded-lg border ${
                    importResult.success 
                      ? "bg-emerald-50 border-emerald-200" 
                      : "bg-red-50 border-red-200"
                  }`}>
                    <p className={`font-medium ${
                      importResult.success ? "text-emerald-700" : "text-red-700"
                    }`}>
                      {importResult.message}
                    </p>
                    
                    {importResult.details && (
                      <div className="mt-3 space-y-2 text-sm">
                        {/* Registros añadidos */}
                        {Object.entries(importResult.details.added || {}).filter(([_, count]) => count > 0).length > 0 && (
                          <div>
                            <p className="font-medium text-emerald-600">Registros añadidos:</p>
                            <ul className="ml-4 list-disc">
                              {Object.entries(importResult.details.added)
                                .filter(([_, count]) => count > 0)
                                .map(([collection, count]) => (
                                  <li key={collection}>{collection}: {count}</li>
                                ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Registros omitidos */}
                        {Object.entries(importResult.details.skipped || {}).filter(([_, count]) => count > 0).length > 0 && (
                          <div>
                            <p className="font-medium text-amber-600">Registros omitidos (ya existían):</p>
                            <ul className="ml-4 list-disc">
                              {Object.entries(importResult.details.skipped)
                                .filter(([_, count]) => count > 0)
                                .map(([collection, count]) => (
                                  <li key={collection}>{collection}: {count}</li>
                                ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Errores */}
                        {importResult.details.errors?.length > 0 && (
                          <div>
                            <p className="font-medium text-red-600">Errores:</p>
                            <ul className="ml-4 list-disc text-red-600">
                              {importResult.details.errors.slice(0, 5).map((error, idx) => (
                                <li key={idx}>{error}</li>
                              ))}
                              {importResult.details.errors.length > 5 && (
                                <li>... y {importResult.details.errors.length - 5} errores más</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Información */}
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div className="text-sm text-slate-600">
                    <p className="font-medium text-slate-700 mb-2">Notas importantes:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>La copia de seguridad se exporta en formato Excel (.xlsx)</li>
                      <li>Cada pestaña del Excel corresponde a una colección de datos</li>
                      <li>Al restaurar, solo se añaden registros con IDs que no existan previamente</li>
                      <li>Los registros existentes NO se modifican ni sobrescriben</li>
                      <li>Se recomienda hacer copias de seguridad periódicas</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Almacenamiento */}
        <TabsContent value="almacenamiento">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-purple-600" />
                Gestión de Almacenamiento
              </CardTitle>
              <CardDescription>
                Administra los archivos almacenados en el sistema. 
                Los documentos nuevos se guardan en MongoDB GridFS (persistente).
                Los documentos rotos (sin archivo) pueden eliminarse desde aquí.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StorageManagement />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Empresa */}
      <Dialog open={empresaDialogOpen} onOpenChange={setEmpresaDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEmpresa ? "Editar Empresa" : "Nueva Empresa"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={empresaForm.nombre}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, nombre: e.target.value })}
                  placeholder="VEXPA COMMODITIES ESPAÑA S.L."
                />
              </div>
              <div className="space-y-2">
                <Label>CIF/NIF *</Label>
                <Input
                  value={empresaForm.cif}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, cif: e.target.value })}
                  placeholder="B-72448970"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>RGSEAA (Registro Sanitario)</Label>
              <Input
                value={empresaForm.rgseaa || ""}
                onChange={(e) => setEmpresaForm({ ...empresaForm, rgseaa: e.target.value })}
                placeholder="12.XXXXX/XX"
              />
            </div>
            <div className="space-y-2">
              <Label>Cuenta Bancaria (IBAN)</Label>
              <Input
                value={empresaForm.cuenta_bancaria || ""}
                onChange={(e) => setEmpresaForm({ ...empresaForm, cuenta_bancaria: e.target.value })}
                placeholder="ES77 2100 7982 9202 0008 5659"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>Registro Mercantil</Label>
              <Input
                value={empresaForm.registro_mercantil || ""}
                onChange={(e) => setEmpresaForm({ ...empresaForm, registro_mercantil: e.target.value })}
                placeholder="Registro Mercantil de VALENCIA Hoja V-206206 Tomo 11227 Folio 185"
              />
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Textarea
                value={empresaForm.direccion}
                onChange={(e) => setEmpresaForm({ ...empresaForm, direccion: e.target.value })}
                placeholder="Dirección completa"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={empresaForm.telefono}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, telefono: e.target.value })}
                  placeholder="+34 XXX XXX XXX"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={empresaForm.email}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, email: e.target.value })}
                  placeholder="email@empresa.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Logo de la empresa</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setEmpresaLogoFile(e.target.files[0])}
              />
              {/* Vista previa del logo actual o del archivo seleccionado */}
              {empresaLogoFile ? (
                <div className="mt-2 p-2 bg-slate-50 rounded border">
                  <p className="text-xs text-slate-500 mb-1">Vista previa:</p>
                  <img 
                    src={URL.createObjectURL(empresaLogoFile)} 
                    alt="Nuevo logo" 
                    className="h-16 object-contain"
                  />
                </div>
              ) : empresaForm.logo_url ? (
                <div className="mt-2 p-2 bg-slate-50 rounded border">
                  <p className="text-xs text-slate-500 mb-1">Logo actual:</p>
                  <img 
                    src={getUploadUrl(empresaForm.logo_url)} 
                    alt="Logo actual" 
                    className="h-16 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmpresaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEmpresa}>
              {editingEmpresa ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Logo */}
      <Dialog open={logoDialogOpen} onOpenChange={setLogoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLogo ? "Editar Logo" : "Nuevo Logo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={logoForm.nombre}
                onChange={(e) => setLogoForm({ ...logoForm, nombre: e.target.value })}
                placeholder="LG Global Seafood"
              />
            </div>
            <div className="space-y-2">
              <Label>Asignar a *</Label>
              <Select 
                value={logoForm.tipo} 
                onValueChange={(v) => setLogoForm({ ...logoForm, tipo: v, entidad_id: "" })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="proveedor">Proveedor</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {logoForm.tipo === "proveedor" && (
              <div className="space-y-2">
                <Label>Proveedor *</Label>
                <Select 
                  value={logoForm.entidad_id} 
                  onValueChange={(v) => setLogoForm({ ...logoForm, entidad_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {logoForm.tipo === "cliente" && (
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select 
                  value={logoForm.entidad_id} 
                  onValueChange={(v) => setLogoForm({ ...logoForm, entidad_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Subir imagen</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files[0])}
              />
            </div>
            <div className="space-y-2">
              <Label>O introducir URL</Label>
              <Input
                value={logoForm.url}
                onChange={(e) => setLogoForm({ ...logoForm, url: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveLogo}>
              {editingLogo ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Control Number Dialog */}
      <AlertDialog open={confirmControlDialog} onOpenChange={setConfirmControlDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Número de control existente</AlertDialogTitle>
            <AlertDialogDescription>
              El número de control {config.numero_control_actual} ya ha sido utilizado en un lote anterior.
              ¿Estás seguro de que deseas usar este número? Esto podría causar conflictos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={saveConfig} className="bg-amber-600 hover:bg-amber-700">
              Usar de todos modos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      {/* Dialog Lugar de Destino */}
      <Dialog open={lugarDestinoDialogOpen} onOpenChange={setLugarDestinoDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLugarDestino ? "Editar Lugar de Destino" : "Nuevo Lugar de Destino"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={lugarDestinoForm.nombre}
                onChange={(e) => setLugarDestinoForm({ ...lugarDestinoForm, nombre: e.target.value })}
                placeholder="FRIO CEREZO, S.L."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CIF</Label>
                <Input
                  value={lugarDestinoForm.cif}
                  onChange={(e) => setLugarDestinoForm({ ...lugarDestinoForm, cif: e.target.value })}
                  placeholder="B97292148"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={lugarDestinoForm.telefono}
                  onChange={(e) => setLugarDestinoForm({ ...lugarDestinoForm, telefono: e.target.value })}
                  placeholder="962580800"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Número de Registro / Autorización</Label>
              <Input
                value={lugarDestinoForm.numero_registro}
                onChange={(e) => setLugarDestinoForm({ ...lugarDestinoForm, numero_registro: e.target.value })}
                placeholder="40.27241/V"
              />
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input
                value={lugarDestinoForm.direccion}
                onChange={(e) => setLugarDestinoForm({ ...lugarDestinoForm, direccion: e.target.value })}
                placeholder="C/ PESCADOR 37 46293 BENEIXIDA VALENCIA"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>País</Label>
                <Input
                  value={lugarDestinoForm.pais}
                  onChange={(e) => setLugarDestinoForm({ ...lugarDestinoForm, pais: e.target.value })}
                  placeholder="ESPAÑA"
                />
              </div>
              <div className="space-y-2">
                <Label>Código ISO del País</Label>
                <Input
                  value={lugarDestinoForm.codigo_iso_pais}
                  onChange={(e) => setLugarDestinoForm({ ...lugarDestinoForm, codigo_iso_pais: e.target.value.toUpperCase() })}
                  placeholder="ES"
                  maxLength={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLugarDestinoDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveLugarDestino}>
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Puesto de Control */}
      <Dialog open={puestoControlDialogOpen} onOpenChange={setPuestoControlDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPuestoControl ? "Editar Puesto de Control" : "Nuevo Puesto de Control"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={puestoControlForm.nombre}
                onChange={(e) => setPuestoControlForm({ ...puestoControlForm, nombre: e.target.value })}
                placeholder="ESVLC1-Animal Health"
              />
            </div>
            <div className="space-y-2">
              <Label>Código</Label>
              <Input
                value={puestoControlForm.codigo}
                onChange={(e) => setPuestoControlForm({ ...puestoControlForm, codigo: e.target.value })}
                placeholder="ESVLC1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPuestoControlDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSavePuestoControl}>
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteType} onOpenChange={() => setDeleteType(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar {deleteType?.type === "empresa" ? "empresa" : "logo"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
